/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Multi RemediationComponent
 *
 * @file MultiRemediationComponent.jsx
 * @author Mohamed yasar arabath M
 */

import React, { Fragment, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ChannelsVideoRecommendationCard from '../common/ChannelsVideoComponent/ChannelsVideoCard';
import ChannelsPracticeRecommendationCard from '../common/ChannelsPracticeCard/ChannelsPracticeCard';
import { AssetsContainer, CarouselWrapper } from './styles/MultiRemediationComponentStyles';
import Carousel from '../carousel/Carousel';
import Utils from '../../common/utils';

const MultiRemediationComponent = (props) => {
  const {
    assets,
    type,
    channelId,
    env,
    getUserCallback,
    onSeeInChannelsLinkClick,
    containerProps,
    onInit = () => {}
  } = props;
  const carouselContainerRef = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    onInit();
  }, []);

  useEffect(() => {
    const containerRef = carouselContainerRef.current;
    if (!containerRef) return;

    const updateWidthVar = (width) => {
      const value = Utils.getItemWidth(width);

      const current = containerRef.style.getPropertyValue('--assetsContainer-width');
      if (current === value) return;
      containerRef.style.setProperty('--assetsContainer-width', value);
    };

    const updateArrowPosition = () => {
      const contentEl = containerRef.parentElement;
      const cardRoot = contentEl?.parentElement;
      if (!contentEl || !cardRoot) return;

      const titleEl = cardRoot.querySelector(':scope > div:first-child p');
      if (!titleEl) return;

      const contentRect = contentEl.getBoundingClientRect();
      const titleRect = titleEl.getBoundingClientRect();
      const topPosition = Math.max(0, contentRect.top - titleRect.top);
      const value = `-${Math.floor(topPosition)}px`;
      const current = containerRef.style.getPropertyValue('--slick-dots-position');

      if (current !== value) {
        containerRef.style.setProperty('--slick-dots-position', value);
      }
    };

    const updateLayoutVars = () => {
      updateWidthVar(containerRef.offsetWidth);
      updateArrowPosition();
    };

    updateLayoutVars();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateLayoutVars);
    });

    resizeObserver.observe(containerRef);
    if (containerRef.parentElement) {
      resizeObserver.observe(containerRef.parentElement);
    }
    if (containerRef.parentElement?.parentElement) {
      resizeObserver.observe(containerRef.parentElement.parentElement);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const isReadyToShow = () => assets && assets.length > 0
    && type && type.video
    && type.practice
    && type.video.length > 0
    && type.practice.length > 0;

  const renderVideoAsset = (asset) => {
    if (!type.video.includes(asset.type)) return null;

    return (
      <ChannelsVideoRecommendationCard
        asset={asset}
        env={env}
        user={getUserCallback()}
        channelId={channelId}
        onSeeInChannelsLinkClick={onSeeInChannelsLinkClick}
      />
    );
  };

  const renderPracticeAsset = (asset) => {
    if (!type.practice.includes(asset.type)) return null;

    return (
      <ChannelsPracticeRecommendationCard
        asset={asset}
        channelId={channelId}
        env={env}
        user={getUserCallback()}
        onSeeInChannelsLinkClick={onSeeInChannelsLinkClick}
      />
    );
  };

  const renderAssets = (asset) => {
    if (!type.video.includes(asset.type) && !type.practice.includes(asset.type)) {
      return null;
    }

    return (
      <AssetsContainer>
        {renderVideoAsset(asset)}
        {renderPracticeAsset(asset)}
      </AssetsContainer>
    );
  };

  return isReadyToShow() ? (
    <CarouselWrapper
      ref={carouselContainerRef}
      {...containerProps}
    >
      <Carousel
        ref={carouselRef}
        enableSlider
        enableArrowNav
        dots
        onNavigation={() => {}}
      >
        {assets.map((asset, index) => (
          <Fragment key={asset?.id || `asset-${index}`}>
            {renderAssets(asset)}
          </Fragment>
        ))}
      </Carousel>
    </CarouselWrapper>
  ) : null;
};

MultiRemediationComponent.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.object),
  type: PropTypes.object,
  channelId: PropTypes.string.isRequired,
  env: PropTypes.string,
  getUserCallback: PropTypes.func,
  onSeeInChannelsLinkClick: PropTypes.func,
  containerProps: PropTypes.object,
  gridContainerProps: PropTypes.object,
  onInit: PropTypes.func
};

MultiRemediationComponent.defaultProps = {
  assets: [],
  type: {
    video: ['video-stream'],
    practice: ['multiple-choice']
  },
  env: 'stg',
  getUserCallback: () => ({ userId: 'demo', token: 'demo' }),
  onSeeInChannelsLinkClick: () => {},
  containerProps: {},
  gridContainerProps: {
    spacing: 16,
    columns: {
      xs: 1, sm: 1, md: 2, mlg: 2, lg: 2, xl: 3, xxl: 3
    }
  },
  onInit: () => {}
};

export default MultiRemediationComponent;
